import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Button,
    Card,
    Col,
    Form,
    FormButtonRow,
    FormGroup,
    FormParagraph,
    FormSection,
    IconButton,
    NumberInput,
    PageHeader,
    Row,
    Select,
    Text,
} from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { DefaultLanguage } from '@common/translate/translate';
import type {
    IOrganizationPatientCount,
    IOrganizationPatientCountsWithUpdatableQuarters,
} from '@frontend/components/interfaces/organizationPatientCount';
import type { ValidationErrors } from '@frontend/utils/index';
import { maxPatientCount, minPatientCount } from '@common/constants';
import { ManagerAuthorization } from '@common/enums';
import { formatNumber } from '@common/utils/formatNumber';
import { organizationPatientCountsFrontendSchema } from '@common/validation/organizationPatientCounts';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import { assertHasAuthorization, emptyValidationErrors, hasOneAuthorization, validate } from '@frontend/utils/index';
import { OrganizationPatientCountLogsModal } from './OrganizationPatientCountLogsModal';

export function OrganizationPatientCount() {
    const abortableFetch = useAbortableFetch();
    const [isEditing, setIsEditing] = useState(false);
    const { organizationId } = useParams() as { organizationId: string };
    const [errorsInCurrentPatientCounts, setErrorsInCurrentPatientCounts] =
        useState<ValidationErrors>(emptyValidationErrors);
    const [errorsInNewPatientCounts, setErrorsInNewPatientCounts] = useState<ValidationErrors>(emptyValidationErrors);
    const [minUpdatableQuarter, setMinUpdatableQuarter] = useState<string>();
    const [currentPatientCounts, setCurrentPatientCounts] = useState<IOrganizationPatientCount[]>([]);
    const [addableDates, setAddableDates] = useState<string[]>([]);
    const [isAddCountOptionVisible, setIsAddCountOptionVisible] = useState(false);
    const [newQuarter, setNewQuarter] = useState<string | null>();
    const [newCount, setNewCount] = useState<number | null>(null);

    const canEdit = hasOneAuthorization([
        ManagerAuthorization.organizationAddEdit,
        ManagerAuthorization.contractFundingDataAddEdit,
    ]);

    assertHasAuthorization([ManagerAuthorization.organizationRead, ManagerAuthorization.contractFundingDataAddEdit]);

    useEffect(() => {
        loadOrganizationPatientCount();
    }, []);

    return (
        <>
            <PageHeader title={getSelectedLanguage().organization.patientCount} />
            <Card>
                <Card.Transition type={isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        !isEditing && canEdit ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={getSelectedLanguage().organization.organizationPatientCount}
                />
                <Card.Body>{renderCountsAddEdit()}</Card.Body>
                {!isEditing && (
                    <OrganizationPatientCountLogsModal
                        currentPatientCounts={currentPatientCounts}
                        organizationId={organizationId}
                    />
                )}
            </Card>
        </>
    );

    function renderCountsAddEdit() {
        return (
            <>
                <Text mb={10}>
                    {currentPatientCounts.length === 0
                        ? getSelectedLanguage().organization.noPatientCount
                        : getSelectedLanguage().organization.patientCountExplainer}
                </Text>
                <Form submitOnEnter={false}>
                    <FormSection>
                        {currentPatientCounts.length > 0 && (
                            <Box mb={4}>
                                <Row columnGap={4}>
                                    <Col span={[6, 5, 4]}>
                                        <Text size="sm">{getSelectedLanguage().organization.referenceDate}</Text>
                                    </Col>
                                    <Col span={[6, 5, 3]}>
                                        <Text size="sm">{getSelectedLanguage().waitingTime.total}</Text>
                                    </Col>
                                </Row>
                            </Box>
                        )}
                        <Box
                            display="grid"
                            gap={4}
                        >
                            {currentPatientCounts.map((patientCount, index) => (
                                <Row
                                    align="center"
                                    columnGap={4}
                                    key={patientCount.id}
                                >
                                    <Col span={[6, 5, 4]}>
                                        <Text>{formatDate(patientCount.referenceDate)}</Text>
                                    </Col>
                                    <Col span={[6, 5, 3]}>
                                        {!isEditing ||
                                        Boolean(
                                            minUpdatableQuarter &&
                                                DateTime.from(patientCount.referenceDate).isBefore(minUpdatableQuarter)
                                        ) ? (
                                            <FormParagraph>{displayNumber(patientCount.count)}</FormParagraph>
                                        ) : (
                                            <NumberInput
                                                error={
                                                    errorsInCurrentPatientCounts.has(`${index}.count`)
                                                        ? (getSelectedLanguage().validation[
                                                              errorsInCurrentPatientCounts.get(
                                                                  `${index}.count`
                                                              ) as keyof DefaultLanguage['validation']
                                                          ] as string)
                                                        : undefined
                                                }
                                                label={getSelectedLanguage().organization.numberOfPatients}
                                                maxValue={maxPatientCount}
                                                minValue={minPatientCount}
                                                onChange={(value) => {
                                                    setErrorsInCurrentPatientCounts(
                                                        errorsInCurrentPatientCounts.remove(`${index}.count`)
                                                    );
                                                    handleChange(value, patientCount);
                                                }}
                                                value={patientCount.count}
                                            />
                                        )}
                                    </Col>
                                </Row>
                            ))}
                        </Box>
                    </FormSection>
                    {isAddCountOptionVisible && renderAddCount()}
                </Form>
                {isEditing && renderButtonRow()}
            </>
        );
    }

    function displayNumber(number: number | null) {
        if (number === null) {
            return '';
        }

        if (isEditing) {
            return number.toString();
        }

        return formatNumber(number);
    }

    function renderButtonRow() {
        return (
            <>
                {isAddCountOptionVisible === false && addableDates.length > 0 && (
                    <Button
                        iconLeft="add"
                        onClick={() => {
                            setIsAddCountOptionVisible(true);
                        }}
                        sx={{ mt: 4 }}
                        variant="link"
                    >
                        {getSelectedLanguage().organization.addCount}
                    </Button>
                )}
                <FormButtonRow>
                    <Button
                        onClick={async () => setStateToReadMode()}
                        variant="outline"
                    >
                        {getSelectedLanguage().common.cancel}
                    </Button>
                    <Button onClick={async () => await handleSubmit()}>{getSelectedLanguage().common.save}</Button>
                </FormButtonRow>
            </>
        );
    }

    function renderAddCount() {
        return (
            <FormSection title={getSelectedLanguage().organization.newCount}>
                <FormGroup
                    hasErrors={errorsInNewPatientCounts.has('0.referenceDate')}
                    isRequired={true}
                    label={getSelectedLanguage().organization.referenceDate}
                >
                    <Select
                        error={
                            errorsInNewPatientCounts.has('0.referenceDate')
                                ? (getSelectedLanguage().validation[
                                      errorsInNewPatientCounts.get(
                                          '0.referenceDate'
                                      ) as keyof DefaultLanguage['validation']
                                  ] as string)
                                : undefined
                        }
                        onChange={(item) => {
                            if (item?.value) {
                                setErrorsInNewPatientCounts(errorsInNewPatientCounts.remove('0.referenceDate'));
                                setNewQuarter(item.value);
                            }
                        }}
                        options={addableDates.map((date) => ({ label: formatDate(date), value: date }))}
                        placeholder={getSelectedLanguage().organization.patientCountReferenceDateExplainer}
                        value={
                            newQuarter
                                ? {
                                      label: formatDate(newQuarter),
                                      value: newQuarter,
                                  }
                                : null
                        }
                    />
                </FormGroup>
                <FormGroup
                    hasErrors={errorsInNewPatientCounts.has('0.count')}
                    isRequired={true}
                    label={getSelectedLanguage().waitingTime.total}
                >
                    <NumberInput
                        error={
                            errorsInNewPatientCounts.has('0.count')
                                ? (getSelectedLanguage().validation[
                                      errorsInNewPatientCounts.get('0.count') as keyof DefaultLanguage['validation']
                                  ] as string)
                                : undefined
                        }
                        label={getSelectedLanguage().organization.numberOfPatients}
                        maxValue={maxPatientCount}
                        minValue={minPatientCount}
                        onChange={(value) => {
                            setErrorsInNewPatientCounts(errorsInNewPatientCounts.remove('0.count'));
                            setNewCount(value);
                        }}
                        value={newCount}
                    />
                </FormGroup>
            </FormSection>
        );
    }

    function handleChange(count: number | null, patientCount: IOrganizationPatientCount) {
        setCurrentPatientCounts(
            currentPatientCounts.map((currentPatientCount) => {
                if (currentPatientCount.id && currentPatientCount.id === patientCount.id) {
                    return {
                        ...currentPatientCount,
                        count,
                    };
                }

                return currentPatientCount;
            })
        );
    }

    function formatDate(date: string | Date) {
        return DateTime.from(date).format('D MMMM YYYY');
    }

    async function setStateToReadMode() {
        setIsEditing(false);
        setIsAddCountOptionVisible(false);
        setNewCount(null);
        setNewQuarter(null);
        setErrorsInCurrentPatientCounts(emptyValidationErrors);
        setErrorsInNewPatientCounts(emptyValidationErrors);

        await loadOrganizationPatientCount();
    }

    async function handleSubmit() {
        setErrorsInCurrentPatientCounts(emptyValidationErrors);
        setErrorsInNewPatientCounts(emptyValidationErrors);

        const { item: validatedPatientCounts, errors: currentPatientCountErrors } = validate(
            organizationPatientCountsFrontendSchema,
            currentPatientCounts
        );

        if (!validatedPatientCounts) {
            setErrorsInCurrentPatientCounts(currentPatientCountErrors);

            return;
        }

        if (!minUpdatableQuarter) {
            return;
        }

        const updatedPatientCounts = validatedPatientCounts.filter((count) =>
            DateTime.from(count.referenceDate).isSameOrAfter(minUpdatableQuarter)
        );

        if (isAddCountOptionVisible) {
            const { item: validatedNewPatientCount, errors: newPatientCountErrors } = validate(
                organizationPatientCountsFrontendSchema,
                [
                    {
                        organizationId: organizationId,
                        referenceDate: newQuarter && DateTime.from(newQuarter).format('YYYY-MM-DD'),
                        count: newCount,
                    },
                ]
            );

            if (!validatedNewPatientCount) {
                setErrorsInNewPatientCounts(newPatientCountErrors);

                return;
            }

            updatedPatientCounts.push(...validatedNewPatientCount);
        }

        const url = `/api/organizations/${organizationId}/organizationPatientCounts`;
        await abortableFetch.postJson(url, updatedPatientCounts);
        await setStateToReadMode();
    }

    async function loadOrganizationPatientCount() {
        const url = `/api/organizations/${organizationId}/organizationPatientCounts`;
        const result = (await abortableFetch.getJson(url)).json.item as IOrganizationPatientCountsWithUpdatableQuarters;

        setMinUpdatableQuarter(result.minUpdatableQuarter);
        setAddableDates(result.addableQuarters);
        setCurrentPatientCounts(result.patientCounts);
    }
}
